import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Form, Popconfirm, Space } from "antd";
import { useCallback, useContext, useState } from "react";
import { useIntl } from "react-intl";
import generateLocalId from "utils/generateLocalId";
import useFormFieldMeta from "hooks/useFormFieldMeta";
import {
  getSubfieldErrors,
  mergeNamePaths,
  namePathStartsWith,
} from "utils/formUtils";
import { useTicketTemplateLazyQuery } from "@ticketingplatform/api/dist/ticketTemplate";
import DisabledContext from "antd/es/config-provider/DisabledContext";

import { TicketPoolFormValue } from "../TicketPoolForm/types";
import { pickTicketPoolFormFields } from "../../utils";

import {
  TicketPoolListFieldValue,
  UseTicketPoolListField,
  UseTicketPoolModal,
  UseTicketTemplateModal,
} from "./types";
import { TicketTemplatePickerFormValues } from "./partials/TicketTemplatePickerForm/types";

export const useTicketPoolModal: UseTicketPoolModal = (props) => {
  const [isOpened, setIsOpened] = useState(false);

  const open = useCallback(() => {
    setIsOpened(true);
  }, []);

  const close = useCallback(() => setIsOpened(false), []);

  const onFinish = useCallback(
    (value: TicketPoolListFieldValue) => {
      props?.onFinish?.(value);
      close();
    },
    [close, props]
  );

  return { isOpened, setIsOpened, open, close, onFinish };
};

export const useTicketTemplateModal: UseTicketTemplateModal = (props) => {
  const [isOpened, setIsOpened] = useState(false);

  const open = useCallback(() => {
    setIsOpened(true);
  }, []);

  const close = useCallback(() => setIsOpened(false), []);

  const onFinish = useCallback(
    async (value: TicketTemplatePickerFormValues) => {
      await props?.onFinish?.(value);
      close();
    },
    [close, props]
  );

  return { isOpened, setIsOpened, open, close, onFinish };
};

export const useTicketPoolListField: UseTicketPoolListField = ({
  name,
  value: dataSource,
  onChange,
  asSignUpOptions,
} = {}) => {
  const intl = useIntl();
  const disabled = useContext(DisabledContext);
  const [getTicketTemplate] = useTicketTemplateLazyQuery();
  const [form] = Form.useForm<TicketPoolFormValue>();
  const [ticketTemplatePickerForm] =
    Form.useForm<TicketTemplatePickerFormValues>();
  const [editIndex, setEditIndex] = useState<number>();

  const { fieldErrors } = useFormFieldMeta((pathName) =>
    namePathStartsWith(pathName, name)
  );

  const handleFinish = useCallback(
    (value: TicketPoolListFieldValue) => {
      if (!onChange) {
        return;
      }

      if (editIndex !== undefined && dataSource) {
        onChange([
          ...dataSource.slice(0, editIndex),
          {
            id: dataSource[editIndex].id,
            ...value,
          },
          ...dataSource.slice(editIndex + 1),
        ]);
      } else {
        onChange([
          ...(dataSource || []),
          {
            id: generateLocalId(),
            ...value,
          },
        ]);
      }
    },
    [dataSource, editIndex, onChange]
  );

  const handleTicketTemplateFinish = useCallback(
    async (value: TicketTemplatePickerFormValues) => {
      if (!onChange) {
        return;
      }

      const {
        ticketTemplate: { id: ticketTemplateId },
      } = value;

      const { data } = await getTicketTemplate({
        variables: { id: ticketTemplateId },
      });

      if (!data || !data.ticketTemplate) {
        return;
      }

      const {
        ticketTemplate: { ticketPools },
      } = data;

      onChange([
        ...(dataSource || []),
        ...(ticketPools?.nodes || []).map((ticketPool) => ({
          ...pickTicketPoolFormFields(ticketPool),
          id: generateLocalId(),
          hasAnyTicketSold: undefined,
        })),
      ]);
    },
    [dataSource, getTicketTemplate, onChange]
  );

  const { isOpened, open, close, onFinish } = useTicketPoolModal({
    onFinish: handleFinish,
  });
  const {
    close: ticketTemplateModalClose,
    isOpened: ticketTemplateModalIsOpened,
    open: ticketTemplateModalOpen,
    onFinish: ticketTemplateModalonFinish,
  } = useTicketTemplateModal({
    onFinish: handleTicketTemplateFinish,
  });

  const handleAdd = useCallback(() => {
    form.setFieldsValue({
      name: null,
      price: asSignUpOptions ? 0 : null,
      venueFee: asSignUpOptions ? 0 : null,
      quantity: null,
      maxTicketsPerOrder: 0,
      minTicketsPerOrder: 0,
      availableBeforeDays: null,
      availableBeforeTime: null,
      expireBeforeDays: null,
      expireBeforeTime: null,
      description: null,
      isAutoApprove: !asSignUpOptions,
      isScannerOnly: false,
    });
    setEditIndex(undefined);
    open();
  }, [asSignUpOptions, form, open]);
  const onTicketTemplateAdd = useCallback(() => {
    ticketTemplatePickerForm.setFieldsValue({
      ticketTemplate: undefined,
    });
    ticketTemplateModalOpen();
  }, [ticketTemplateModalOpen, ticketTemplatePickerForm]);

  const handleEdit = useCallback(
    (value: TicketPoolListFieldValue, index: number) => {
      form.setFieldsValue(value);
      setEditIndex(index);
      open();

      const namePath = mergeNamePaths(name, index);
      const fields = getSubfieldErrors(fieldErrors, namePath);

      // Need to run `form.setFields()` in `setTimeout` cause Ant.design needs
      // to be initialized and it does not support initial error props
      setTimeout(() => {
        form.setFields(fields);
      }, 1);
    },
    [fieldErrors, form, name, open]
  );

  const handleDelete = useCallback(
    (_value: TicketPoolListFieldValue, index: number) => {
      if (!onChange || !dataSource) {
        return;
      }

      onChange([...dataSource.slice(0, index), ...dataSource.slice(index + 1)]);
    },
    [dataSource, onChange]
  );

  return {
    table: {
      dataSource,
      asSignUpOptions,
      actionsColumn: disabled
        ? undefined
        : {
            title: intl.formatMessage({
              defaultMessage: "Action",
            }),
            key: "action",
            render: (_, value, index) => (
              <Space size="middle">
                <EditOutlined onClick={() => handleEdit(value, index)} />
                {!value.hasAnyTicketSold && (
                  <Popconfirm
                    title={intl.formatMessage(
                      {
                        defaultMessage:
                          "Are you sure you want to delete Ticket: {name}?",
                      },
                      { name: value.name }
                    )}
                    onConfirm={() => handleDelete(value, index)}
                  >
                    <DeleteOutlined />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
    },
    modal: { open: isOpened, onCancel: close },
    ticketTemplateModal: {
      open: ticketTemplateModalIsOpened,
      onCancel: ticketTemplateModalClose,
    },
    form: {
      form,
      onFinish,
    },
    ticketTemplatePickerForm: {
      form: ticketTemplatePickerForm,
      onFinish: ticketTemplateModalonFinish,
    },
    onAdd: handleAdd,
    onTicketTemplateAdd,
  };
};
